/**
 * DepartmentPicker — the department FK field for People, with inline create.
 *
 * People departments are `user_groups` rows of type `department` — the records
 * `/admin/groups?type=department` manages. Before WI 39f6ce30 this was a plain
 * `Select` offering only "None", and nothing in the People module could create a
 * department: an HR admin setting up a fresh tenant had no reachable way to fill
 * the headcount-by-department dashboard widget. Creating one is now part of
 * assigning one, which is how BambooHR, HiBob and Rippling all do it.
 *
 * Inline create follows the platform standard (STYLE_GUIDE § Inline Create From a
 * Combo): "+ Create [name]" opens DepartmentQuickCreateDialog with the typed name
 * pre-filled, and the created row is selected on confirm. It never POSTs straight
 * from the popover — a combo that creates on click gives the user nothing to
 * review and no way to back out of a typo.
 *
 * `POST /groups` is `require_admin`, so the create affordance is gated on the
 * tenant-admin flag rather than letting a member find out via a 403.
 */
import { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Building2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { BidiText, EntityPickerBase } from '../../../shared/components/common';
import { filterAndSortBySearch } from '../../../shared/utils/searchScore';
import { useAuth } from '../../../shared/auth/AuthContext';
import { useDepartments } from '../hooks/useGroups';
import { DepartmentQuickCreateDialog } from './DepartmentQuickCreateDialog';

interface Department {
  id: string;
  name: string;
}

interface DepartmentPickerProps {
  /** Selected department id, or '' when none. */
  value: string;
  onChange: (departmentId: string) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Hide the inline create action (filter contexts, where creating makes no sense). */
  hideAddNew?: boolean;
}

export function DepartmentPicker({
  value,
  onChange,
  label,
  placeholder,
  disabled,
  className,
  hideAddNew = false,
}: DepartmentPickerProps) {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createDialogName, setCreateDialogName] = useState('');

  const { data: departmentsData } = useDepartments();

  const canCreate = Boolean(user?.is_admin) && !hideAddNew;

  const departments = useMemo<Department[]>(() => {
    const all = (departmentsData?.data ?? []) as Department[];
    return filterAndSortBySearch(all, search, (d) => d.name);
  }, [departmentsData, search]);

  const close = useCallback(() => {
    setOpen(false);
    setSearch('');
  }, []);

  const handleSelect = useCallback(
    (departmentId: string) => {
      onChange(departmentId);
      close();
    },
    [onChange, close],
  );

  const handleClear = useCallback(() => {
    onChange('');
    close();
  }, [onChange, close]);

  const handleAddNew = useCallback(() => {
    setCreateDialogName(search.trim());
    close();
    setShowCreateDialog(true);
  }, [search, close]);

  const handleCreated = useCallback(
    (department: { id: string }) => {
      onChange(department.id);
      setShowCreateDialog(false);
    },
    [onChange],
  );

  // With nothing typed the dialog opens on an empty name field, so the row reads
  // as the generic "add" rather than promising to create a specific department.
  const addNewLabel = search.trim()
    ? t('pickers.createDepartmentWithName', { name: search.trim() })
    : t('pickers.addNewDepartment');

  return (
    <>
      <EntityPickerBase<Department>
        value={value}
        items={departments}
        getItemLabel={(department) => department.name}
        renderItem={(department, isSelected) => (
          <>
            <Check className={cn('me-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')} />
            <Building2 className="me-2 h-4 w-4 text-muted-foreground shrink-0" />
            <BidiText className="truncate">{department.name}</BidiText>
          </>
        )}
        label={label}
        placeholder={placeholder ?? t('pickers.selectDepartment')}
        searchPlaceholder={t('pickers.searchDepartments')}
        emptyMessage={
          canCreate ? t('pickers.noDepartmentsFound') : t('pickers.noDepartmentsAdminOnly')
        }
        disabled={disabled}
        className={className}
        search={search}
        onSearchChange={setSearch}
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setSearch('');
        }}
        onSelect={handleSelect}
        // "None" is a clear action, so it only belongs on a field that has
        // something to clear. Rendering it unconditionally also kept the list
        // non-empty forever, which suppressed the empty-state message a member on
        // a department-less tenant most needs to see.
        allowClear={Boolean(value)}
        onClear={handleClear}
        addNewLabel={addNewLabel}
        onAddNew={canCreate ? handleAddNew : undefined}
      />
      {canCreate && (
        <DepartmentQuickCreateDialog
          open={showCreateDialog}
          onClose={() => setShowCreateDialog(false)}
          defaultName={createDialogName}
          onCreated={handleCreated}
        />
      )}
    </>
  );
}
